import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '../../../../components/Icon/Icon';
import type { BlogHero } from '../../../../mock-data/types';
import { HeroDetailsSheet } from '../HeroDetailsSheet';
import { GenerateButton } from '../GenerateButton';
import { GeneratingWash } from '../GeneratingWash';
import {
  Root,
  Label,
  DropZone,
  DropHint,
  EmptyStack,
  PreviewRow,
  ThumbFrame,
  Thumb,
  Meta,
  Summary,
  MetaLine,
  MetaLabel,
  MetaValue,
  Actions,
  ActionButton,
  DestructiveButton,
  Status,
  ErrorText,
} from './HeroField.styles';

export type HeroFieldProps = {
  hero?: BlogHero;
  onChange: (hero?: BlogHero) => void;
  /**
   * Stores the picked file and resolves to its public URL. Rejects with the
   * message to show (the blog service returns upload refusals as UI copy).
   */
  onUpload: (file: File) => Promise<string>;
  /**
   * Draws a hero for this article, alt text and caption included. Absent when
   * generation is off for the environment, or when there is no article yet to
   * draw one FOR: the button is then not offered rather than offered and refused.
   */
  onGenerate?: () => Promise<void>;
  /** Credit cost of a hero draw; shown on the Generate buttons once priced. */
  generateCost?: number;
  /** Lumio is drawing the image right now. */
  generating?: boolean;
  /** Something is being written somewhere on the page: every control goes inert. */
  busy?: boolean;
};

/**
 * The article's hero image as one row of the article-details form: a thumbnail,
 * a two-line read-out of the alt text and caption, and the four things an author
 * does to it (edit that copy, swap the file, have Lumio draw one, drop it).
 *
 * The actions sit on the picture's BOTTOM EDGE rather than under the summary, so
 * the two halves read as one block instead of as a picture with some buttons
 * floating beside it.
 *
 * The picked file goes to the blog service, which stores it in the media bucket
 * and answers with the CDN URL; only that URL reaches the draft. Articles saved
 * before this existed carry a `data:` URL instead and keep rendering: nothing
 * rewrites them, and a hero is only replaced when the author picks a new file or
 * asks for a new one.
 */
export const HeroField = ({
  hero,
  onChange,
  onUpload,
  onGenerate,
  generateCost,
  generating = false,
  busy = false,
}: HeroFieldProps): React.ReactElement => {
  const { t } = useTranslation();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [detailsOpen, setDetailsOpen] = React.useState(false);

  const inert = busy || uploading;

  const pick = (): void => inputRef.current?.click();

  const onFile = async (file: File): Promise<void> => {
    setUploading(true);
    setError(null);
    try {
      const url = await onUpload(file);
      onChange({ url, alt: hero?.alt ?? '', caption: hero?.caption ?? '' });
    } catch (err) {
      // The previous hero (if any) stays on the draft: a failed replace must
      // not cost the author the image they already had.
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
    }
  };

  const draw = async (): Promise<void> => {
    if (!onGenerate) return;
    setError(null);
    try {
      await onGenerate();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <Root>
      <Label text="Hero image" tag="span" />

      {hero ? (
        <PreviewRow>
          <ThumbFrame>
            <Thumb src={hero.url} alt={hero.alt || t('Hero image preview')} />
            {generating ? <GeneratingWash label={t('Lumio is drawing the hero image')} /> : null}
          </ThumbFrame>
          <Meta>
            <Summary>
              <MetaLine>
                <MetaLabel text="Alt text" tag="span" />
                <MetaValue text={hero.alt || 'Not set'} tag="span" $missing={!hero.alt} />
              </MetaLine>
              <MetaLine>
                <MetaLabel text="Caption" tag="span" />
                <MetaValue text={hero.caption || 'Not set'} tag="span" $missing={!hero.caption} />
              </MetaLine>
            </Summary>
            <Actions>
              {/* No icons: four short labels side by side read faster than four
                  glyphs, and the only unambiguous one would be the bin, which
                  would make Remove the loudest of the set. */}
              <ActionButton
                label="Edit alt and caption"
                variant="outline"
                onClick={() => setDetailsOpen(true)}
                disabled={inert}
              />
              <ActionButton label="Replace" variant="outline" onClick={pick} disabled={inert} />
              {onGenerate ? (
                <GenerateButton
                  label={t('Generate')}
                  name={t('Generate the hero image')}
                  cost={generateCost}
                  disabled={inert}
                  onClick={() => void draw()}
                />
              ) : null}
              <DestructiveButton
                label="Remove"
                variant="outline"
                onClick={() => onChange(undefined)}
                disabled={inert}
              />
            </Actions>
          </Meta>
        </PreviewRow>
      ) : (
        <EmptyStack>
          {/* The wash is a SIBLING of the drop zone, not a child: a button may
              only contain phrasing content, and the overlay is a div with a
              status region in it. `EmptyStack` is the positioned host. */}
          <DropZone type="button" onClick={pick} disabled={inert}>
            {generating ? null : <Icon name="plus-circle" size={24} />}
            <DropHint
              text={generating ? t('Lumio is drawing your hero image') : t('Upload a hero image')}
              tag="span"
            />
          </DropZone>
          {generating ? <GeneratingWash label={t('Lumio is drawing the hero image')} /> : null}
          {onGenerate && !generating ? (
            <GenerateButton label={t('Generate hero image')} cost={generateCost} disabled={inert} onClick={() => void draw()} />
          ) : null}
        </EmptyStack>
      )}

      {uploading ? <Status text="Uploading the image…" tag="span" /> : null}
      {error ? <ErrorText text={error} tag="span" /> : null}

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void onFile(file);
          event.target.value = '';
        }}
      />

      {detailsOpen && hero ? (
        <HeroDetailsSheet hero={hero} onSave={onChange} onClose={() => setDetailsOpen(false)} />
      ) : null}
    </Root>
  );
};

export default HeroField;
